import type { ComponentProps } from "react";
import Link from "next/link";
import Image from "next/image";

import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Subline } from "@/components/typography/Subline";
import { Headline } from "@/components/typography/Headline";
import { Tag } from "@/components/typography/Tag";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import { cn } from "@/lib/utils";

type ImageCardProps = {
  tagline?: string;
  headline: string;
  text?: string;
  href: string;
  image?: string;
  tags?: (number | string)[];
  size?: "normal" | "large";
  mask?: boolean;
  locked?: boolean;
} & ComponentProps<"div">;

const ImageCard = ({
  tagline,
  headline,
  text,
  href,
  image = "/images/shape.svg",
  tags = [],
  size = "normal",
  mask = false,
  locked = false,
  ...rest
}: ImageCardProps) => (
  <Card
    className={cn(
      "bg-sand-10 rounded-3xl border-none overflow-hidden card-rotate relative cursor-pointer",
      rest.className
    )}
  >
    <div className="relative">
      <Image
        src={image}
        alt=""
        width={400}
        height={300}
        className="w-full h-40 md:h-52 object-cover"
        style={{ clipPath: mask ? "url(#mask1)" : undefined }}
      />
      {locked && (
        <div className="absolute top-3 right-3">
          <MemberLockIcon locked />
        </div>
      )}
    </div>
    <CardContent className="p-6 space-y-4">
      <div>
        <header>
          <Subline>{tagline}</Subline>
          <p className="text-headline font-bold">
            {headline}
          </p>
        </header>
        <CardDescription>{text}</CardDescription>
      </div>
      <div className="flex justify-start items-center gap-2 flex-wrap relative z-20">
        {tags?.map((name: number | string, index: number) => (
          <Tag
            key={index}
            label={name.toLocaleString()}
            href={`/search?tag=${name}`}
            asButton
          />
        ))}
      </div>
    </CardContent>
    <Link
      href={href}
      className="absolute inset-0 z-10"
      aria-label={headline || "Weitere Informationen"}
    />
  </Card>
);

export { ImageCard };
